import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';

import { UserService } from "app/services/user.service";
import { User } from 'app/models/user';
import { DatatableComponent } from "@swimlane/ngx-datatable";
import { map, flatMap, catchError, finalize } from 'rxjs/operators';
import { Team } from 'app/models/team';
import { TeamService } from 'app/services/team.service';
import { Observable, of } from 'rxjs';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import {Location} from '@angular/common';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { ActivatedRoute } from '@angular/router';
import * as _ from "lodash";

enum filterActiveType {
  ALL = 1,
  ACTIVE,
  INACTIVE
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, AfterViewInit {
  
  filterActiveOptions = [
    { value: filterActiveType.ALL, label: 'Any', selected:true},
    { value: filterActiveType.ACTIVE, label: 'Active Only'},
    { value: filterActiveType.INACTIVE, label: 'Inactive Only'}
  ];
  filterActive: filterActiveType = filterActiveType.ACTIVE;
  filterTeamIds: number[] = [];

  users:User[] = [];
  unfilteredUsers:User[] = [];
  
  loadingUserIds:number[] = [];

  teams: Team[] = [];
  allowedTeamsForCreate: Team[] = [];
  allowedTeamsForUpdate: Team[] = [];

  tableLoadingIndicator: boolean = true;

  isAddingUser: boolean = false;
  isEditingUser: boolean = false;

  editingUser: User = null;

  tableLimitSelect: Array<any>;
  selectedTableLimit: number = 5;

  @ViewChild(DatatableComponent, { static: true }) table: DatatableComponent;
  @ViewChild('editModal', { static: true }) editModal: ModalDirective;
  @ViewChild('addModal', { static: true }) addModal: ModalDirective;

  addForm: FormGroup;
  addFormOverlay: FormGroup;
  editForm: FormGroup;

  usersFilter: string = null;

  constructor(private location: Location, public userService: UserService, private _validationHandler: ValidationHandler,
    private teamService: TeamService, private formBuilder: FormBuilder, private ecoDocsTitleService: EcoDocsTitleService,
    private activatedRoute: ActivatedRoute, private toastService: ToastService) { 

    this.addForm = this.formBuilder.group({
      name: [''],
      email: ['', [Validators.required, Validators.email]],
      team_ids: [[]],
    });

    this.addFormOverlay = this.formBuilder.group({
      name: [''],
      email: ['', [Validators.required, Validators.email]],
      team_ids: [[]],
    });

    this.editForm = this.formBuilder.group({
      id: [undefined],
      name: [''],
      email: ['', [Validators.required, Validators.email]],
      team_ids: [[]],
    });

    this.tableLimitSelect = [5, 10, 20, 30, 50, 100];
    this.selectedTableLimit = 5;
  }

  ngOnInit() {
    this.ecoDocsTitleService.setTitle( 'Manage Users', false );

    this.tableLoadingIndicator = true;
    this.userService.getAll().subscribe(users => {

      this.users = users;
      this.unfilteredUsers = [...users];

      setTimeout(() => { this.tableLoadingIndicator = false; }, 100);
      this.updateFilter();
    });

    this.allowedTeamsForCreate = [];
    this.allowedTeamsForUpdate = [];
    this.teamService.getAll(false).subscribe((t) => {
      this.teams = t;
      if (this.userService.getPermissions('super.admin.users.create').all) {
        this.allowedTeamsForCreate = t;
      } else if (this.userService.getPermissions('super.admin.users.create').teams) {
        this.allowedTeamsForCreate = _.intersectionBy(t, this.userService.getPermissions('super.admin.users.create').teams, 'id');
      }

      if (this.userService.getPermissions('super.admin.users.create.update').all) {
        this.allowedTeamsForUpdate = t;
      } else if (this.userService.getPermissions('super.admin.users.create.update').teams) {
        this.allowedTeamsForUpdate = _.intersectionBy(t, this.userService.getPermissions('super.admin.users.create.update').teams, 'id');
      }
    });
  }

  ngAfterViewInit(): void {
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      const search: string = queryParams['search']? queryParams['search']: '';
  
      if (search.length > 0) {
        setTimeout(() => {
          this.usersFilter = search;
          this.updateFilter();            
        }, 1);
      }
    });    
  }

  innerWidth() {
    return window.innerWidth;
  }
  onAddUser(frm: FormGroup) {
    this.isAddingUser = true;
    if(!frm.valid)
    {
      this._validationHandler.onSubmitCheck(frm);
      this.isAddingUser = false;
      return ;
    }
    let data = frm.value;
    if (!data['name'])
      data['name']= data['email'];

    this.userService.add(<User[]>[frm.value]).pipe(finalize(() => {
      this.isAddingUser = false;
      this.addModal.hide();
    })).subscribe(u => {
      this.unfilteredUsers.unshift(...u);
      // re-apply filter
      this.updateFilter();

      // refresh data
      this.users = [...this.users];
      frm.reset();

      this.toastService.success('User(s) Added!');
    }, err => {
      /** @todo handle error */
      this._validationHandler.serverValidationHandler(err, frm);
      this.isAddingUser = false;

      this.toastService.error('Error Adding User(s)!');
    });
  }
  onSaveEditUser() {
    this.isEditingUser = true;
    if(!this.editForm.valid)
    {
      this._validationHandler.onSubmitCheck(this.editForm);
      this.isEditingUser = false;
      return ;
    }
    
    this.userService.update(this.editForm.value).pipe(finalize(()=>{
      this.isEditingUser = false;
      this.editingUser = null;
    })).subscribe(u => {

      // refresh data
      var userIndex = this.unfilteredUsers.findIndex((v, i, o) => {
        return v.id == u.id;
      });
      
      if (userIndex != -1)
        this.unfilteredUsers[userIndex] = u;

      this.updateFilter();
      this.users = [...this.users];
      
      this.editModal.hide();
      this.isEditingUser = false;
      this.editingUser = null;
      this.editForm.reset();
    }, err => {
      /** @todo handle error */
      this.isEditingUser = false;
      this.editingUser = null;
      this._validationHandler.serverValidationHandler(err, this.editForm);
      
    });
  }

  isLeadingTeam(user, team) {
    return user.leading_teams.find(t => t.name == team.name);
  }

  onEditUser(user: User) {
    this.editingUser = user;
    var team_ids = [];
    user.teams.forEach(v => {
      team_ids.push(v.id);
    });
    this.editForm.setValue({
      id: user.id,
      name: (user.name && user.name != user.email) ? user.name : '',
      email: user.email,
      team_ids: team_ids
    });

    this.editModal.show();
  }
  onResendInvite(user) {
    if (!this.loadingUserIds.includes(user.id)) {
      this.loadingUserIds.push(user.id);
      this.userService.resendInvite(user.id).pipe((finalize(() => {
        this.loadingUserIds = this.loadingUserIds.filter(id => id != user.id);
        this.users = [...this.users];
      }))).subscribe(() => {
        this.toastService.success('Invite sent!');
      }, () => {
        this.toastService.error('Error sending invite!');
      });
    }
  }
  onCloseEdit() {
    this.editModal.hide();
    this.editingUser = null;
    this.isEditingUser = false;
  }

  onActivateChanged(event, user) {
    if (event.target.checked) {
      if (!this.loadingUserIds.includes(user.id)) {
        this.userService.restoreUser(user.id).pipe(map(x => {
          /** @todo handle error */
          user.deleted_at = null;
          this.loadingUserIds = this.loadingUserIds.filter(id => id != user.id);
          this.users = [...this.users];
        })).subscribe();
        this.loadingUserIds.push(user.id);
      }
    } else {
      if (!this.loadingUserIds.includes(user.id)) {
        this.userService.deactivateUser(user.id).subscribe(x => {
          /** @todo handle error */
          user.deleted_at = Date.now();
          this.loadingUserIds = this.loadingUserIds.filter(id => id != user.id);
          this.users = [...this.users];
        });
        this.loadingUserIds.push(user.id);
      }
    }
  }
  updateFilter() {
    const val = (this.usersFilter)? this.usersFilter.toLowerCase(): null;
    
    // filter our data
    const temp = this.unfilteredUsers.filter((user) => {
      var isTeamFilterOk: boolean = (this.filterTeamIds.length == 0);
    
      this.filterTeamIds.forEach((t) =>{
        if (user.teams.find((ut:Team) => {
          return t == ut.id;
        }))
          isTeamFilterOk = true;
      });

      return (user.name.toLowerCase().indexOf(val) !== -1 || user.email.toLowerCase().indexOf(val) !== -1 || !val) &&
              (this.filterActive == filterActiveType.ALL || (this.filterActive == filterActiveType.ACTIVE && !user.deleted_at) || (this.filterActive == filterActiveType.INACTIVE && user.deleted_at)) &&
              (isTeamFilterOk);
    });

    // update the rows
    this.users = temp;
    
    // Whenever the filter changes, always go back to the first page
    this.table.offset = 0;
  }
}
